import { Backdrop, Button, CircularProgress, DialogContent, Grid, Tooltip } from "@mui/material";
import FileDropzone from "common/components/FileDropzone/FileDropzone";
import FilterBar from "common/components/FilterBar/FilterBar";
import GenericAlert from "common/components/GenericAlert/GenericAlert";
import GenericDialog from "common/components/GenericDialog/GenericDialog";
import MenuActions from "common/components/MenuActions/MenuActions";
import SelectProvinceAndLocation from "common/components/SelectProvinceAndLocation/SelectProvinceAndLocation";
import { ApiService } from "common/services/ApiService";
import { useAuth } from "context/AuthContext";
import { camelizeObject } from "helpers/TextHelpers";
import { UseAlert } from "hooks/UseAlert";
import DialogDatasetContent from "modules/activity/pages/Datasets/DialogDatasetContent";
import moment from "moment";
import Papa from "papaparse";
import { useCallback, useState } from "react";
import { VISIBILITY, VISIBILITY_MAP } from "utils/constants";

const ERROR_CODE_DUPLICATE = "1";

const ERROR_CODE_MAP = {
    [ERROR_CODE_DUPLICATE]:
        "Ya existe un dataset cargado con ese nombre, por favor modifíque el nombre de su dataset antes de subirlo",
};

const Datasets = (props) => {
    const { auth } = useAuth();
    const [openLoader, setOpenLoader] = useState(false);
    const [datasets, setDatasets] = useState([]);

    const [rowCount, setRowCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [filter, setFilter] = useState("");
    const [extraFilter, setExtraFilter] = useState({
        visibility: auth.selectedRole?.name === "superadmin" ? "" : VISIBILITY.public,
    });
    const [page, setPage] = useState(1);
    const [extraFilterComponent, setExtraFilterComponent] = useState({});

    const [file, setFile] = useState(null);
    const [open, setOpen] = useState(false);
    const [inputsFromCSV, setInputsFromCSV] = useState([]);
    const [inputsFromUser, setInputsFromUser] = useState({});
    const [hasHeaders, setHasHeaders] = useState(false);
    const [openDialogAddDataset, setOpenDialogAddDataset] = useState(false);
    const [openDialogShowDataset, setOpenDialogShowDataset] = useState(false);
    const [actualDataset, setActualDataset] = useState({});

    const {
        alertText,
        showAlert,
        setAlertText,
        setShowAlert,
        confirmAction,
        executeAction,
        openDialog,
        setOpenDialog,
        dialogContentText,
        setSeverity,
        severity,
        action,
    } = UseAlert();

    const [visibilityFile, setVisibilityFile] = useState(VISIBILITY.public);
    const [descriptionFile, setDescriptionFile] = useState("");
    const [visibilityMap, setVisibilityMap] = useState({});

    const deleteDataset = (id) => {
        const dataToUpdate = {
            deleted: true,
        };
        updateStaticDataSource(id, dataToUpdate);
    };

    const showDataset = (id, hide) => {
        const dataToUpdate = {
            hide,
        };
        updateStaticDataSource(id, dataToUpdate);
    };

    const downloadPdf = async (name) => {
        try {
            const res = await ApiService.downloadStaticDataSourcePdf(name);

            if (res.status === 200) {
                const url = window.URL.createObjectURL(
                    new Blob([res.data], { type: "application/pdf" })
                );
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                console.error("Error al descargar el dataset:", res);
            }
        } catch (error) {
            console.error("Error en la descarga:", error);
        }
    };

    const downloadDataset = async (name) => {
        try {
            const res = await ApiService.downloadStaticDataSource(name);
            if (res.status === 200) {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                console.error("Error al descargar el dataset:", res);
            }
        } catch (error) {
            console.error("Error en la descarga:", error);
        }
    };

    const updateStaticDataSource = async (id, data) => {
        try {
            await ApiService.patchStaticDataSource(id, data);
            getDatasets(filter, extraFilter, page, pageSize);
        } catch (error) {
            console.log(error);
        }
    };

    const renderActionButtons = (params) => {
        const options = [
            {
                label: !visibilityMap[params.row.id] ? "Archivar" : "Desarchivar",
                handleClick: () => {
                    setActualDataset(params.row);
                    const isVisible = !visibilityMap[params.row.id];
                    confirmAction(
                        "show",
                        () => showDataset(params.row.id, isVisible),
                        isVisible
                            ? "¿Está seguro que desea archivar este dataset?"
                            : "¿Está seguro que desea desarchivar este dataset?"
                    );
                },
                disabled: !params.row.canHide,
            },
            {
                label: "Descargar",
                handleClick: () => {
                    setActualDataset(params.row);
                    if (params.row.name.includes(".pdf")) {
                        downloadPdf(params.row.name);
                    } else downloadDataset(params.row.name);
                },
            },
            {
                label: "Eliminar",
                handleClick: () => {
                    setActualDataset(params.row);
                    confirmAction(
                        "delete",
                        () => deleteDataset(params.row.id),
                        "¿Está seguro que desea eliminar este dataset?"
                    );
                },
                disabled: !params.row.canDelete,
            },
            {
                label: "Ver info",
                handleClick: () => {
                    setActualDataset(params.row);
                    setOpenDialogShowDataset(!openDialogShowDataset);
                },
            },
        ];
        return <MenuActions options={options} />;
    };

    const datasetsColumns = [
        {
            field: "name",
            headerName: "Titulo",
            width: 200,
            renderCell: (params) => (
                <Tooltip title={params.row.name}>
                    <p style={{ overflow: "hidden" }}>{params.row.name}</p>
                </Tooltip>
            ),
        },
        {
            field: "owner",
            headerName: "Propietario",
            width: 200,
            renderCell: (params) => (
                <Tooltip title={params.row.owner}>
                    <p style={{ overflow: "hidden" }}>{params.row.owner}</p>
                </Tooltip>
            ),
        },
        {
            field: "establishment",
            headerName: "Establecimiento",
            width: 200,
            renderCell: (params) => (
                <Tooltip title={params.row.establishment}>
                    <p style={{ overflow: "hidden" }}>{params.row.establishment}</p>
                </Tooltip>
            ),
        },
        { field: "createdAt", headerName: "Fecha de Creación", width: 180 },
        { field: "fileSize", headerName: "Tamaño de Archivo", width: 180 },
        {
            field: "actions",
            headerName: "Acciones",
            width: 200,
            hideable: true,
            align: "center",
            headerAlign: "center",
            renderCell: renderActionButtons,
        },
    ];

    const visibilityOptions = () => [
        {
            value: "",
            label: "Todo",
            show: auth.selectedRole?.name === "superadmin",
        },
        {
            value: VISIBILITY.public,
            label: "Establecimiento",
            show: auth.selectedRole?.name !== "superadmin",
        },
        {
            value: VISIBILITY.private,
            label: "Privado",
            show: auth.selectedRole?.name !== "superadmin",
        },
        {
            value: VISIBILITY.community,
            label: "Comunidad",
            show: true,
        },
        {
            value: VISIBILITY.hide,
            label: "Archivado",
            show: true,
        },
    ];

    const extraFilterList = [
        {
            type: "radio",
            key: "visibility",
            items: visibilityOptions(),
        },
    ];

    const componentEstablishmentFilterBar = (
        <SelectProvinceAndLocation
            handleChangeProvince={() => {}}
            handleChangeLocation={() => {}}
            handleChangeEstablishment={(establishment) => {
                setExtraFilterComponent({
                    ...extraFilterComponent,
                    establishment: establishment?.id,
                });
            }}
            showEstablishments={true}
            gridSizes={{
                province: { xs: 12, lg: 4, sm: 4 },
                location: { xs: 12, lg: 4, sm: 4 },
                establishment: { xs: 12, lg: 4, sm: 4 },
            }}
        />
    );

    // const extraFilterCallback = (filters) => {
    //     const aux = { ...filters };
    //     delete aux.visibility;
    //     setExtraFilterComponent(aux);
    // };

    const getDatasets = async (filter = "", extraFilter = {}, page = 1, pageSize = 10) => {
        setOpenLoader(true);
        try {
            const auxFilter = { ...extraFilter };
            const hide = extraFilter.visibility === VISIBILITY.hide;

            if (!auxFilter.establishment) {
                if (extraFilter.visibility === VISIBILITY.community) {
                    auxFilter.establishment = null;
                } else {
                    auxFilter.establishment =
                        auth.selectedRole?.name === "superadmin"
                            ? ""
                            : auth.selectedEstablishment?.id || "";
                }
            }

            if (hide) {
                delete auxFilter.visibility;
            }

            const params = {
                ...auxFilter,
                hide,
                search: filter,
            };

            const res = await ApiService.getStaticDataSources(page, pageSize, params);

            const dataCleaned = res.data.results.map((d) => {
                const camelizedObject = camelizeObject(d);
                return {
                    ...camelizedObject,
                    createdAt: moment(camelizedObject.createdAt).format("DD/MM/YYYY"),
                    establishment: camelizedObject.establishment?.name,
                    owner: camelizedObject.owner?.email,
                    fileSize: camelizedObject.fileSize ? `${camelizedObject.fileSize} MB` : "",
                    headers: camelizedObject.headers ? camelizedObject.headers : "",
                    visibility: VISIBILITY_MAP[camelizedObject.visibility] || VISIBILITY.public,
                };
            });

            let aux = {};
            dataCleaned.forEach((d) => {
                aux[d.id] = d.hide;
            });

            setVisibilityMap(aux);
            setRowCount(res.data.count);
            setPageSize(pageSize);
            setFilter(filter);
            setExtraFilter(extraFilter);
            setPage(page);

            setDatasets(dataCleaned);
        } catch (error) {
            handleShowAlert("error", "Ocurrió un error, intente nuevamente!");
        } finally {
            setOpenLoader(false);
        }
    };

    const readFile = (file) => {
        return new Promise((resolve, reject) =>
            Papa.parse(file, {
                worker: true,
                complete: (results) => {
                    resolve(results.data.filter((r) => !r[0].includes("#"))[0]);
                },
                error: (err) => reject(err),
            })
        );
    };

    const onDrop = useCallback(async (acceptedFiles) => {
        setOpen(true);
        if (acceptedFiles?.length > 0) {
            setFile(acceptedFiles[0]);
            if (acceptedFiles[0].type.includes("csv"))
                await readFile(acceptedFiles[0])
                    .then((data) => setInputsFromCSV(data))
                    .catch((error) => console.log(error));
        }
        setOpen(false);
    }, []);

    const handleShowAlert = (variant, msg) => {
        setSeverity(variant);
        setAlertText(msg);
        setShowAlert(true);
        setTimeout(() => {
            setShowAlert(false);
            setSeverity("");
            setAlertText("msg");
        }, 5000);
    };

    const handleClickButton = async () => {
        setOpen(true);

        const formData = new FormData();
        if (file === null) {
            handleShowAlert("error", "Por favor seleccione un archivo");
            setOpen(false);
            return;
        }

        const hide = visibilityFile === VISIBILITY.hide;

        formData.append("file", file);
        formData.append("has_headers", hasHeaders);
        formData.append("visibility", visibilityFile ? visibilityFile : VISIBILITY.public);
        formData.append("hide", hide);
        formData.append("description", descriptionFile);

        if (auth.selectedRole?.name !== "superadmin" && auth.selectedEstablishment?.id) {
            formData.append("establishment", auth.selectedEstablishment?.id);
        }

        if (file.type.includes("csv")) {
            formData.append("headers_file", JSON.stringify(Object.values(inputsFromUser)));
            formData.append("headers_user", JSON.stringify([]));
            // if (!hasHeaders) {
            //     formData.set("headers_file", JSON.stringify(Object.values(inputsFromUser)));
            // }
        }

        try {
            await ApiService.uploadFile(formData);
            handleShowAlert("success", "Dataset cargado con éxito!");
            setInputsFromCSV([]);
            setInputsFromUser({});
            setHasHeaders(false);
            setVisibilityFile(VISIBILITY.public);
            setDescriptionFile("");
            getDatasets(filter, extraFilter, page, pageSize);
            setOpenDialogAddDataset(false);
        } catch (error) {
            const errorMessage =
                ERROR_CODE_MAP[error.response?.data.error_code] ||
                "Ocurrió un error, intente nuevamente!";
            handleShowAlert("error", errorMessage);
        }
        setFile(null);
        setOpen(false);
    };

    return (
        <Grid container spacing={2} sx={{ flexDirection: "column" }}>
            <Grid item xs={12}>
                <h1>Datasets</h1>
            </Grid>
            {auth.selectedRole?.name !== "superadmin" && (
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        mb={3}
                        onClick={() => {
                            setOpenDialogAddDataset(true);
                        }}
                    >
                        Subir Dataset
                    </Button>
                </Grid>
            )}
            <Grid item xs={12}>
                <FilterBar
                    labelSearch="Buscar dataset"
                    onPageChange={getDatasets}
                    openLoader={openLoader}
                    rowCount={rowCount}
                    columns={datasetsColumns}
                    rows={datasets}
                    pageSize={pageSize}
                    enableExtraFilter
                    extraFilterParent={extraFilter}
                    extraFilterList={extraFilterList}
                    extraFilterComponent={extraFilterComponent}
                    extraFilterTitle="Fuentes de datos cargadas en Alfadatizando"
                    component={
                        extraFilter.visibility === VISIBILITY.community
                            ? componentEstablishmentFilterBar
                            : undefined
                    }
                />
            </Grid>
            <GenericDialog
                title="Agregar Dataset"
                open={openDialogAddDataset}
                setOpen={setOpenDialogAddDataset}
                handleSave={() => handleClickButton()}
                disabledSabeButton={!file}
                onClose={() => {
                    setFile(null);
                    setInputsFromCSV([]);
                    setInputsFromUser({});
                    setHasHeaders(false);
                    setVisibilityFile(VISIBILITY.public);
                    setDescriptionFile("");
                }}
                mandatoryText={false}
            >
                <DialogContent>
                    <FileDropzone
                        file={file}
                        inputsFromCSV={inputsFromCSV}
                        onDrop={onDrop}
                        open={open}
                        setInputsFromUser={setInputsFromUser}
                        inputsFromUser={inputsFromUser}
                        hasHeaderCallback={(value) => setHasHeaders(value)}
                        setVisibilityCallback={(value) => setVisibilityFile(value)}
                        setDescripctionCallback={(value) => setDescriptionFile(value)}
                    />
                </DialogContent>
            </GenericDialog>
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: 3,
                }}
                open={openLoader}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <GenericDialog
                title="Confirmación"
                open={openDialog}
                setOpen={setOpenDialog}
                handleSave={executeAction}
                saveButtonText="Sí"
                mandatoryText={false}
            >
                <DialogContent>{dialogContentText}</DialogContent>
            </GenericDialog>
            <GenericDialog
                title="Información del dataset"
                open={openDialogShowDataset}
                setOpen={setOpenDialogShowDataset}
                saveButton={false}
                cancelButton={false}
                mandatoryText={false}
            >
                <DialogDatasetContent dataset={actualDataset} />
            </GenericDialog>
            {showAlert && (
                <GenericAlert
                    severity={severity}
                    text={alertText}
                    style={{
                        position: "fixed",
                        zIndex: "9999999999999999",
                        top: "50%",
                        left: "30%",
                    }}
                />
            )}
        </Grid>
    );
};

export default Datasets;
